import React, { useEffect, useReducer } from 'react';

import clsx from 'clsx';
import update from 'immutability-helper';
import {
    makeStyles, Button,
} from '@material-ui/core';
import { isEmpty } from 'lodash';
import KeyStore from 'utils/KeyStore';
import { Form } from 'react-bootstrap';
import ModalUtils from 'utils/ModalUtils';
import AddIcon from '@material-ui/icons/Add';
import { FetchPolicy } from 'utils/enum/Core';
import ButtonStyles from 'styles/theme/Button';
import Permissions from 'utils/enum/Permissions';
import DeleteIcon from '@material-ui/icons/Delete';
import { useMutation, useQuery } from '@apollo/client';
import TagQuery from 'services/graphQL/query/setting/TagQuery';
import TagMutation from 'services/graphQL/mutate/crm/TagMutation';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import TagTable from 'components/modules/settings/widgets/tag/TagTable';
import TagDialog from 'components/modules/settings/widgets/tag/TagDialog';
import BorderColorOutlinedIcon from '@material-ui/icons/BorderColorOutlined';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '& > button': {
            marginRight: theme.spacing(1),
            width: '95px',
        },
    },
    tableHeight: {
        display: 'contents',
    },
    ...ButtonStyles.getStyle(theme),
}));
const keyStore = new KeyStore();
const ACTION_TYPE = {
    SET_RECORDS: 'setRecords',
    ON_OPEN_DIALOG: 'onOpenDialog',
    ON_CLOSE_DIALOG: 'onCloseDialog',
    ON_CHANGE_VALUE: 'onChangeValue',
    ON_SET_SELECT_RECORD: 'onSetSelectRecord',
    TOGGLE_DIALOG_CONFIRM: 'toggleDialogConfirm',
};
const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.SET_RECORDS:
        return update(state, {
            records: { $set: action.payload.data },
            totalCount: { $set: action.payload.totalCount },
        });
    case ACTION_TYPE.ON_SET_SELECT_RECORD:
        return update(state, {
            selectedRow: { $set: action.payload.record },
            open: { $set: action.payload.open },
            isEditing: { $set: action.payload.isEditing },
        });
    case ACTION_TYPE.ON_CLOSE_DIALOG:
        return update(state, {
            open: { $set: false },
            selectedRow: { $set: null },
            isEditing: { $set: false },
        });
    case ACTION_TYPE.ON_OPEN_DIALOG:
        return update(state, {
            open: { $set: true },
            isEditing: { $set: action.payload?.isEditing || false },
        });
    case ACTION_TYPE.TOGGLE_DIALOG_CONFIRM:
        if (action.payload) {
            return update(state, {
                confirmDialogOpen: { $set: action.payload },
            });
        }

        return update(state, {
            confirmDialogOpen: { $set: action.payload },
            selectedRow: { $set: null },
        });
    case ACTION_TYPE.ON_CHANGE_VALUE:
        return update(state, {
            [action.field]: { $set: action.payload },
            selectedRow: { $set: null },
            records: { $set: [] },
            totalCount: { $set: 0 },
        });
    default:
        return state;
    }
};

const TagsSetting = () => {
    const classes = useStyles();
    const [state, dispatch] = useReducer(reducer, {
        records: [],
        open: false,
        active: true,
        totalCount: 0,
        selectedRow: null,
        confirmDialogOpen: false,
    });
    const CRM_SETTING_TAG_WRITE = keyStore.hasPermission(Permissions.CRM_SETTING_TAG_WRITE);
    const [deleteTag, { loading: deleting }] = useMutation(TagMutation.DELETE_CRM_TAG);
    // TODO: Change this in the next update (remove refetch function)
    const {
        data, error, loading, refetch,
    } = useQuery(TagQuery.GET_TAG_BY_CRM_MODULE, {
        variables: { paginate: { start: 0, limit: 100 }, active: state.active },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (!loading && data?.getTagByCRMModule) {
            dispatch({
                type: ACTION_TYPE.SET_RECORDS,
                payload: data.getTagByCRMModule,
            });
        }
    }, [data, loading]);

    if (error) {
        ModalUtils.errorMessage(null, error?.message);
    }

    const onChangeValue = (field, payload) => {
        dispatch({
            type: ACTION_TYPE.ON_CHANGE_VALUE,
            field,
            payload,
        });
    };

    const toggleDialogConfirm = () => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_DIALOG_CONFIRM,
            payload: !state.confirmDialogOpen,
        });
    };

    const onSelectRow = (selectedRow) => {
        dispatch({
            type: ACTION_TYPE.ON_SET_SELECT_RECORD,
            payload: {
                record: selectedRow,
            },
        });
    };

    const onClose = () => {
        dispatch({
            type: ACTION_TYPE.ON_CLOSE_DIALOG,
        });
    };

    const onEdit = () => {
        dispatch({
            type: ACTION_TYPE.ON_OPEN_DIALOG,
            payload: {
                isEditing: true,
            },
        });
    };

    const onCreate = () => {
        dispatch({
            type: ACTION_TYPE.ON_OPEN_DIALOG,
        });
    };

    const onDelete = async () => {
        try {
            const { tagId } = state.selectedRow;
            const response = await deleteTag({ variables: { tagId } });

            if (!isEmpty(response.data?.deleteCRMTag)) {
                toggleDialogConfirm();
                refetch();
            } else {
                ModalUtils.errorMessage(null, 'Error ');
            }
        } catch (ex) {
            ModalUtils.errorMessage(null, ex);
        }
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                {CRM_SETTING_TAG_WRITE && (
                    <>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onCreate}
                            className={clsx(classes.containedGreen)}
                            startIcon={<AddIcon />}
                        >
                            New
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onEdit}
                            className={clsx(classes.containedInfo)}
                            startIcon={<BorderColorOutlinedIcon />}
                            disabled={isEmpty(state.selectedRow)}
                        >
                            Edit
                        </Button>
                        {state.active
                            && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={toggleDialogConfirm}
                                    className={clsx(classes.containedError)}
                                    startIcon={<DeleteIcon />}
                                    disabled={isEmpty(state.selectedRow)}
                                >
                                    Delete
                                </Button>
                            )}
                    </>
                )}
                <div className="d-flex-center">
                    <Form.Group
                        className="form-check form-check-inline mb-0"
                    >
                        <Form.Check
                            key="radioActive"
                            type="radio"
                            name="radio"
                            id="radioActive"
                            label="Active"
                            value={state.active}
                            checked={state.active}
                            onChange={() => onChangeValue('active', true)}
                        />
                        <Form.Check
                            key="radioInactive"
                            type="radio"
                            name="radio"
                            id="radioInactive"
                            label="In-Active"
                            value={!state.active}
                            checked={!state.active}
                            onChange={() => onChangeValue('active', false)}
                        />
                    </Form.Group>
                </div>
            </div>
            <div className={classes.tableHeight}>
                <TagTable
                    onEdit={onEdit}
                    loading={loading}
                    records={state.records}
                    onSelectRow={onSelectRow}
                    totalCount={state.totalCount}
                    selectedRow={state.selectedRow}
                />
            </div>
            {state.open && (
                <TagDialog
                    open={state.open}
                    onClose={onClose}
                    refetch={refetch}
                    record={state.isEditing ? state.selectedRow : null}
                />
            )}
            {state.confirmDialogOpen && (
                <ConfirmDialog
                    title="Attention!"
                    description={`
                    This tag is currently on one or more opportunities,
                    by deleting this tag you will be removing this tag from them all.
                    Continue?
                `}
                    open={state.confirmDialogOpen}
                    variant="outlined"
                    titlePrimary="Yes"
                    titleSecondary="No"
                    dividerFooter={false}
                    onClose={toggleDialogConfirm}
                    onClickSecondary={toggleDialogConfirm}
                    onClickPrimary={onDelete}
                    disablePrimaryButton={deleting}
                />
            )}
        </div>
    );
};

export default TagsSetting;
